import { useId } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ActionButton,
  Drawer,
  DrawerSubmitting,
  Form,
  FormSection,
  toast,
} from '@core/components';
import type { Customer } from '../../api/customer';
import {
  type CustomerMeasurementFormValues,
  useCustomerForm,
} from '../hooks/useCustomerForm';
import { getFullName } from '../utils/customer';
import { CustomerMeasurementFields } from './CustomerMeasurementFields';

interface CustomerMeasurementsDrawerProps {
  open: boolean;
  customer?: Customer | null;
  onClose: () => void;
}

const CustomerMeasurementsDrawer = ({
  open,
  customer,
  onClose,
}: CustomerMeasurementsDrawerProps) => {
  const formId = useId();
  const { measurementDefaultValues, saveMeasurements } =
    useCustomerForm(customer);

  const onSubmit = async (data: CustomerMeasurementFormValues) => {
    if (!customer) {
      return;
    }
    try {
      await saveMeasurements(data);
      toast.success('Measurements saved');
      onClose();
    } catch (error) {
      toast.apiError('Failed to save measurements.', error);
    }
  };

  return (
    <Drawer
      open={open && Boolean(customer)}
      onClose={onClose}
      width={400}
      zIndex={(theme) => theme.zIndex.modal + 1}
      title={
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary">
            Measurements
          </Typography>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {getFullName(customer) || 'Customer'}
          </Typography>
        </Box>
      }
      footer={({ isSubmitting }) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <ActionButton
            variant="outline"
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant="primary"
            type="submit"
            form={formId}
            loading={isSubmitting}
          >
            Save
          </ActionButton>
        </Box>
      )}
    >
      <Form<CustomerMeasurementFormValues>
        id={formId}
        defaultValues={measurementDefaultValues}
        onSubmit={onSubmit}
        resetOn={open}
      >
        {(methods) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <DrawerSubmitting isSubmitting={methods.formState.isSubmitting} />
            <FormSection title="Alb measurements">
              <CustomerMeasurementFields group="alb" columns="1fr 1fr" />
            </FormSection>
            <FormSection title="Chasuble measurements">
              <CustomerMeasurementFields group="chasuble" columns="1fr 1fr" />
            </FormSection>
            <FormSection title="Mitre measurements">
              <CustomerMeasurementFields group="mitre" columns="1fr 1fr" />
            </FormSection>
          </Box>
        )}
      </Form>
    </Drawer>
  );
};

export default CustomerMeasurementsDrawer;
